import { MenuItem } from "@headlessui/react"
import { Fragment } from "react"
import noEventsIllustration from "assets/illustrations/noEvents.svg"
import { Icon, Menu } from "materia"
import { useTranslation } from "react-i18next"
import { modifiers } from "ui-tools"
import { WhenVisible } from "utility-components"
import {
  type NotificationData,
  NotificationItem,
  NotificationItemSkeleton,
} from "../NotificationItem"
import s from "./NotificationMenu.module.scss"

export type NotificationMenuProps = {
  notifications: NotificationData[]
  unreadCount: number
  onNotificationRead?: (notificationId: string) => void
  onNotificationClick?: (notification: NotificationData) => void
  onMarkAllAsRead?: () => void
  onLoadMore: () => void
  isLoading?: boolean
  isLoadingMore?: boolean
  skeletonCount?: number
  error?: string
  emptyMessage?: string
}

export const NotificationMenu = ({
  notifications,
  unreadCount,
  onNotificationRead,
  onNotificationClick,
  onMarkAllAsRead,
  onLoadMore,
  isLoading,
  isLoadingMore,
  skeletonCount,
  error,
  emptyMessage,
}: NotificationMenuProps) => {
  return (
    <Menu>
      <NotificationTrigger unreadCount={unreadCount} />

      <Menu.Content
        anchor="bottom end"
        className={[s.mobileFullscreen, s.notificationMenu__menuOverride].join(" ")}
      >
        <div className={s.notificationMenu__content}>
          <NotificationMenuHeader
            unreadCount={unreadCount}
            onMarkAllAsRead={onMarkAllAsRead}
          />
          <NotificationList
            notifications={notifications}
            onNotificationRead={onNotificationRead}
            onNotificationClick={onNotificationClick}
            onLoadMore={onLoadMore}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            skeletonCount={skeletonCount}
            error={error}
            emptyMessage={emptyMessage}
          />
        </div>
      </Menu.Content>
    </Menu>
  )
}

type NotificationTriggerProps = {
  unreadCount: number
}

const NotificationTrigger = ({ unreadCount }: NotificationTriggerProps) => {
  const { t } = useTranslation()

  return (
    <Menu.Trigger asChild={true} dataTestId="notification-menu-trigger">
      <button
        className={s.notificationMenu__trigger}
        aria-label={
          unreadCount > 0
            ? t("notifications.UNREAD_COUNT_ARIA", { count: unreadCount })
            : t("notifications.TITLE")
        }
      >
        <Icon icon="Bell" size="large" />
        {unreadCount > 0 && (
          <span className={s.notificationMenu__badge}>
            <span className={s.notificationMenu__count}>
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          </span>
        )}
      </button>
    </Menu.Trigger>
  )
}

type NotificationMenuHeaderProps = {
  unreadCount: number
  onMarkAllAsRead?: () => void
}

const NotificationMenuHeader = ({
  unreadCount,
  onMarkAllAsRead,
}: NotificationMenuHeaderProps) => {
  const { t } = useTranslation()

  return (
    <div className={s.notificationMenu__header}>
      <MenuItem
        as="button"
        className={s.notificationMenu__closeButton}
        aria-label={t("notifications.CLOSE")}
      >
        <Icon icon="Close" />
      </MenuItem>

      <h2 className={s.notificationMenu__title}>{t("notifications.TITLE")}</h2>

      <button
        className={modifiers(
          s,
          "notificationMenu__pill",
          unreadCount > 0 ? "unread" : "seen"
        )}
        onClick={unreadCount > 0 ? onMarkAllAsRead : undefined}
        disabled={unreadCount === 0}
        aria-label={
          unreadCount > 0
            ? t("notifications.MARK_ALL_AS_READ_ARIA")
            : t("notifications.ALL_READ_ARIA")
        }
      >
        <Icon icon="Checkmark" size="small" />
        {unreadCount > 0
          ? t("notifications.MARK_AS_SEEN")
          : t("notifications.UP_TO_DATE")}
      </button>
    </div>
  )
}

type NotificationListProps = {
  notifications: NotificationData[]
  onNotificationRead?: (notificationId: string) => void
  onNotificationClick?: (notification: NotificationData) => void
  onLoadMore: () => void
  isLoading?: boolean
  isLoadingMore?: boolean
  skeletonCount?: number
  error?: string
  emptyMessage?: string
}

const NotificationList = ({
  notifications,
  onNotificationRead,
  onNotificationClick,
  onLoadMore,
  isLoading = false,
  isLoadingMore = false,
  skeletonCount = 0,
  error = "",
  emptyMessage,
}: NotificationListProps) => {
  const { t } = useTranslation()

  const getContent = () => {
    if (isLoading) {
      return (
        <li className={s.notificationList__loadingText}>
          {t("notifications.LOADING")}
        </li>
      )
    }

    if (error) {
      console.error("Error in NotificationMenu: ", error)
      return <li>{t("notifications.ERROR")}</li>
    }

    if (notifications.length === 0) {
      return (
        <li className={s.notificationList__empty}>
          <img
            className={s.notificationList__emptyIllustration}
            src={noEventsIllustration}
            alt=""
            aria-hidden="true"
          />
          <p className={s.notificationList__emptyText}>
            {emptyMessage ?? t("notifications.EMPTY")}
          </p>
        </li>
      )
    }

    return (
      <>
        {notifications.map((notification) => {
          const item = (
            <NotificationItem
              notification={notification}
              onRead={onNotificationRead}
              onActivate={onNotificationClick}
            />
          )

          // Wrap only notifications that deep-link somewhere in a MenuItem, so
          // clicking one closes the menu (Headless closes on MenuItem activation).
          // Path-less notifications stay plain — clicking marks them read and
          // keeps the menu open for triaging.
          return notification.data.path ? (
            <MenuItem as={Fragment} key={notification.id}>
              {item}
            </MenuItem>
          ) : (
            <Fragment key={notification.id}>{item}</Fragment>
          )
        })}
      </>
    )
  }

  return (
    <div
      className={modifiers(
        s,
        "notificationMenu__listContainer",
        notifications.length === 0 ? "empty" : undefined
      )}
    >
      <ul className={s.notificationList}>
        {getContent()}
        {isLoadingMore &&
          Array.from({ length: skeletonCount }, (_, i) => (
            <NotificationItemSkeleton key={i} />
          ))}
      </ul>

      <WhenVisible
        fetchNextPage={onLoadMore}
        isFetchingNextPage={isLoadingMore}
      />
    </div>
  )
}
